import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import FloatingActionButton from '../../components/ui/FloatingActionButton';
import { useToast, ToastContainer } from '../../components/ui/Toast';

// Import components
import DishCard from './components/DishCard';
import DishFilters from './components/DishFilters';
import DishForm from './components/DishForm';
import DishDetailModal from './components/DishDetailModal';
import BulkImportModal from './components/BulkImportModal';

const DishLibrary = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError, showInfo } = useToast();

  // State management
  const [dishes, setDishes] = useState([]);
  const [filteredDishes, setFilteredDishes] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    cuisines: [],
    dietaryRestrictions: [],
    prepTimeRanges: [],
    servings: { min: '', max: '' },
    sortBy: 'name'
  });

  // Modal states
  const [showDishForm, setShowDishForm] = useState(false);
  const [showDishDetail, setShowDishDetail] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedDish, setSelectedDish] = useState(null);
  const [editingDish, setEditingDish] = useState(null);

  // Current user mock data
  const currentUser = {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    avatar: "https://images.unsplash.com/photo-1734991032476-bceab8383a59",
    avatarAlt: "Professional headshot of woman with shoulder-length brown hair in business attire"
  };

  // Mock dishes data
  const mockDishes = [
  {
    id: 1,
    name: "Spaghetti Carbonara",
    cuisine: "Italian",
    prepTime: 25,
    servings: 4,
    ingredients: ["Spaghetti", "Eggs", "Pancetta", "Parmesan cheese", "Black pepper", "Olive oil"],
    dietaryRestrictions: [],
    instructions: `1. Cook spaghetti in salted boiling water according to package directions until al dente.\n2. While pasta cooks, fry pancetta in a large pan until crispy.\n3. In a bowl, whisk together eggs, grated Parmesan, and black pepper.\n4. Drain pasta, reserving 1 cup of pasta water.\n5. Add hot pasta to the pan with pancetta.\n6. Remove from heat and quickly stir in egg mixture, adding pasta water as needed.\n7. Serve immediately with extra Parmesan and black pepper.`,
    notes: "Traditional Roman recipe. Never add cream! The creaminess comes from the eggs and cheese.",
    image: "https://images.unsplash.com/photo-1663721605989-3bdd2c994190",
    imageAlt: "Creamy spaghetti carbonara with crispy pancetta and black pepper in white ceramic bowl",
    usageCount: 8,
    rating: 5,
    lastUsed: "2024-10-20T19:30:00Z",
    createdAt: "2024-08-15T10:00:00Z",
    updatedAt: "2024-10-20T19:30:00Z",
    isNew: false
  },
  {
    id: 2,
    name: "Chicken Tikka Masala",
    cuisine: "Indian",
    prepTime: 45,
    servings: 6,
    ingredients: ["Chicken breast", "Yogurt", "Tomatoes", "Heavy cream", "Garam masala", "Turmeric", "Garlic", "Ginger", "Onion"],
    dietaryRestrictions: ["gluten-free"],
    instructions: `1. Cut chicken into bite-sized pieces and marinate in yogurt and spices for 30 minutes.\n2. Cook marinated chicken in a hot pan until golden brown.\n3. In the same pan, sauté onions, garlic, and ginger until fragrant.\n4. Add tomatoes and spices, cook until thick.\n5. Stir in cream and cooked chicken.\n6. Simmer for 10-15 minutes until sauce thickens.\n7. Serve with basmati rice and naan bread.`,
    notes: "Popular British-Indian dish. Adjust spice level to taste. Can be made dairy-free with coconut milk.",
    image: "https://images.unsplash.com/photo-1593560704563-f176a2eb61db",
    imageAlt: "Creamy orange chicken tikka masala curry in traditional bowl with rice and cilantro garnish",
    usageCount: 12,
    rating: 4,
    lastUsed: "2024-10-18T18:00:00Z",
    createdAt: "2024-07-20T14:30:00Z",
    updatedAt: "2024-10-18T18:00:00Z",
    isNew: false
  },
  {
    id: 3,
    name: "Mediterranean Quinoa Bowl",
    cuisine: "Mediterranean",
    prepTime: 20,
    servings: 4,
    ingredients: ["Quinoa", "Cucumber", "Cherry tomatoes", "Red onion", "Feta cheese", "Olives", "Lemon", "Olive oil", "Fresh herbs"],
    dietaryRestrictions: ["vegetarian", "gluten-free"],
    instructions: `1. Cook quinoa according to package directions and let cool.\n2. Dice cucumber, halve cherry tomatoes, and thinly slice red onion.\n3. Crumble feta cheese and pit olives.\n4. Make dressing with lemon juice, olive oil, salt, and pepper.\n5. Combine all ingredients in a large bowl.\n6. Toss with dressing and fresh herbs.\n7. Let sit for 10 minutes before serving to allow flavors to meld.`,
    notes: "Perfect for meal prep. Keeps well in the refrigerator for up to 3 days.",
    image: "https://images.unsplash.com/photo-1588471790208-16825e6bcc24",
    imageAlt: "Colorful Mediterranean quinoa bowl with fresh vegetables, feta cheese and herbs in white bowl",
    usageCount: 6,
    rating: 4,
    lastUsed: "2024-10-15T12:30:00Z",
    createdAt: "2024-09-10T11:00:00Z",
    updatedAt: "2024-10-15T12:30:00Z",
    isNew: false
  },
  {
    id: 4,
    name: "Beef Tacos with Avocado Salsa",
    cuisine: "Mexican",
    prepTime: 30,
    servings: 4,
    ingredients: ["Ground beef", "Taco seasoning", "Corn tortillas", "Avocado", "Lime", "Red onion", "Cilantro", "Jalapeño", "Tomato"],
    dietaryRestrictions: ["gluten-free"],
    instructions: `1. Brown ground beef in a large skillet over medium-high heat.\n2. Add taco seasoning and cook according to package directions.\n3. Warm tortillas in a dry pan or microwave.\n4. For salsa: dice avocado, tomato, red onion, and jalapeño.\n5. Mix salsa ingredients with lime juice and cilantro.\n6. Season salsa with salt and pepper to taste.\n7. Serve beef in tortillas topped with avocado salsa.`,
    notes: "Use fresh lime juice to prevent avocado from browning. Add hot sauce for extra heat.",
    image: "https://images.unsplash.com/photo-1605209763272-b5f7265d8ed5",
    imageAlt: "Fresh beef tacos with avocado salsa, cilantro and lime on wooden serving board",
    usageCount: 9,
    rating: 5,
    lastUsed: "2024-10-22T19:00:00Z",
    createdAt: "2024-08-05T16:45:00Z",
    updatedAt: "2024-10-22T19:00:00Z",
    isNew: false
  },
  {
    id: 5,
    name: "Thai Green Curry",
    cuisine: "Asian",
    prepTime: 35,
    servings: 4,
    ingredients: ["Chicken thigh", "Green curry paste", "Coconut milk", "Thai basil", "Eggplant", "Bell peppers", "Fish sauce", "Palm sugar", "Lime leaves"],
    dietaryRestrictions: ["gluten-free", "dairy-free"],
    instructions: `1. Heat coconut milk in a large pan until it begins to separate.\n2. Add green curry paste and fry until fragrant.\n3. Add chicken pieces and cook until nearly done.\n4. Pour in remaining coconut milk and bring to a simmer.\n5. Add vegetables and cook until tender.\n6. Season with fish sauce and palm sugar.\n7. Garnish with Thai basil and serve with jasmine rice.`,
    notes: "Adjust curry paste amount for desired spice level. Fresh curry paste makes a big difference.",
    image: "https://images.unsplash.com/photo-1586128737015-87894872e863",
    imageAlt: "Aromatic Thai green curry with chicken and vegetables in coconut milk broth with fresh basil",
    usageCount: 4,
    rating: 4,
    lastUsed: "2024-10-10T18:30:00Z",
    createdAt: "2024-09-25T13:15:00Z",
    updatedAt: "2024-10-10T18:30:00Z",
    isNew: true
  },
  {
    id: 6,
    name: "French Ratatouille",
    cuisine: "French",
    prepTime: 60,
    servings: 6,
    ingredients: ["Eggplant", "Zucchini", "Bell peppers", "Tomatoes", "Onion", "Garlic", "Herbs de Provence", "Olive oil", "Fresh thyme"],
    dietaryRestrictions: ["vegetarian", "vegan", "gluten-free", "dairy-free"],
    instructions: `1. Dice all vegetables into uniform pieces.\n2. Sauté onions and garlic until translucent.\n3. Add eggplant and cook until softened.\n4. Add bell peppers and zucchini, cook for 10 minutes.\n5. Stir in tomatoes and herbs.\n6. Simmer covered for 30-40 minutes, stirring occasionally.\n7. Season with salt, pepper, and fresh thyme before serving.`,
    notes: "Traditional Provençal stewed vegetable dish. Great as a side or main course with crusty bread.",
    image: "https://images.unsplash.com/photo-1714888070656-56084bef81fc",
    imageAlt: "Rustic French ratatouille with colorful diced vegetables and fresh herbs in ceramic dish",
    usageCount: 3,
    rating: 4,
    lastUsed: "2024-09-28T17:45:00Z",
    createdAt: "2024-09-15T09:30:00Z",
    updatedAt: "2024-09-28T17:45:00Z",
    isNew: true
  }];


  // Initialize dishes
  useEffect(() => {
    setDishes(mockDishes);
  }, []);

  // Filter and sort dishes
  const applyFilters = useMemo(() => {
    let filtered = [...dishes];

    // Search filter
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter((dish) =>
      dish?.name?.toLowerCase()?.includes(searchTerm) ||
      dish?.ingredients?.some((ingredient) => ingredient?.toLowerCase()?.includes(searchTerm)) ||
      dish?.cuisine?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Cuisine filter
    if (filters?.cuisines?.length > 0) {
      filtered = filtered?.filter((dish) =>
      filters?.cuisines?.includes(dish?.cuisine)
      );
    }

    // Dietary restrictions filter
    if (filters?.dietaryRestrictions?.length > 0) {
      filtered = filtered?.filter((dish) =>
      filters?.dietaryRestrictions?.every((restriction) =>
      dish?.dietaryRestrictions?.includes(restriction)
      )
      );
    }

    // Prep time filter
    if (filters?.prepTimeRanges?.length > 0) {
      filtered = filtered?.filter((dish) => {
        return filters?.prepTimeRanges?.some((rangeId) => {
          switch (rangeId) {
            case 'quick':
              return dish?.prepTime <= 30;
            case 'medium':
              return dish?.prepTime > 30 && dish?.prepTime <= 60;
            case 'long':
              return dish?.prepTime > 60;
            default:
              return false;
          }
        });
      });
    }

    // Servings filter
    if (filters?.servings?.min || filters?.servings?.max) {
      filtered = filtered?.filter((dish) => {
        const min = parseInt(filters?.servings?.min) || 0;
        const max = parseInt(filters?.servings?.max) || 999;
        return dish?.servings >= min && dish?.servings <= max;
      });
    }

    // Sort
    filtered?.sort((a, b) => {
      switch (filters?.sortBy) {
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'recent':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'popular':
          return b?.usageCount - a?.usageCount;
        case 'rating':
          return (b?.rating || 0) - (a?.rating || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [dishes, filters]);

  useEffect(() => {
    setFilteredDishes(applyFilters);
  }, [applyFilters]);

  // Event handlers
  const handleAddDish = () => {
    setEditingDish(null);
    setShowDishForm(true);
  };

  const handleEditDish = (dish) => {
    setEditingDish(dish);
    setShowDishForm(true);
    setShowDishDetail(false);
  };

  const handleViewDish = (dish) => {
    setSelectedDish(dish);
    setShowDishDetail(true);
  };

  const handleDeleteDish = (dish) => {
    if (window.confirm(`Are you sure you want to delete "${dish?.name}"?`)) {
      setDishes((prev) => prev?.filter((d) => d?.id !== dish?.id));
      showSuccess(`"${dish?.name}" has been deleted successfully`);
    }
  };

  const handleSaveDish = (dishData) => {
    if (editingDish) {
      // Update existing dish
      setDishes((prev) => prev?.map((dish) =>
      dish?.id === editingDish?.id ? dishData : dish
      ));
      showSuccess(`"${dishData?.name}" has been updated successfully`);
    } else {
      // Add new dish
      setDishes((prev) => [...prev, dishData]);
      showSuccess(`"${dishData?.name}" has been added to your library`);
    }
    setShowDishForm(false);
    setEditingDish(null);
  };

  const handleBulkImport = async (importedDishes) => {
    try {
      // Check for duplicates
      const existingNames = dishes?.map((d) => d?.name?.toLowerCase());
      const newDishes = importedDishes?.filter((dish) =>
      !existingNames?.includes(dish?.name?.toLowerCase())
      );
      const duplicates = importedDishes?.length - newDishes?.length;

      setDishes((prev) => [...prev, ...newDishes]);

      if (duplicates > 0) {
        showInfo(`Imported ${newDishes?.length} dishes. ${duplicates} duplicates were skipped.`);
      } else {
        showSuccess(`Successfully imported ${newDishes?.length} dishes to your library`);
      }
    } catch (error) {
      showError('Failed to import dishes. Please try again.');
    }
  };

  const handleFloatingAction = (actionType) => {
    switch (actionType) {
      case 'add-recipe':
        handleAddDish();
        break;
      case 'import-recipe':
        setShowBulkImport(true);
        break;
      case 'new-event':navigate('/dinner-planning');
        break;
      case 'add-guest':navigate('/guest-management');
        break;
      default:
        break;
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} onLogout={handleLogout} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Dish Library</h1>
              <p className="text-muted-foreground mt-2">
                Manage your recipe collection and track dish usage
              </p>
            </div>
            <div className="hidden lg:flex space-x-3">
              <Button variant="outline" onClick={() => setShowBulkImport(true)}>
                <Icon name="Upload" size={16} className="mr-2" />
                Bulk Import
              </Button>
              <Button onClick={handleAddDish}>
                <Icon name="Plus" size={16} className="mr-2" />
                Add Dish
              </Button>
            </div>
          </div>

          {/* Filters */}
          <DishFilters
            filters={filters}
            onFiltersChange={setFilters}
            resultCount={filteredDishes?.length} />


          {/* Dishes Grid */}
          {filteredDishes?.length > 0 ?
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredDishes?.map((dish) =>
            <DishCard
              key={dish?.id}
              dish={dish}
              onEdit={handleEditDish}
              onDelete={handleDeleteDish}
              onView={handleViewDish} />

            )}
            </div> :

          <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                <Icon name="ChefHat" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No dishes found</h3>
              <p className="text-muted-foreground mb-6">
                {filters?.search || filters?.cuisines?.length > 0 || filters?.dietaryRestrictions?.length > 0 ?
              'Try adjusting your filters or search terms' : 'Start building your recipe collection by adding your first dish'
              }
              </p>
              {dishes?.length === 0 &&
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={handleAddDish}>
                    <Icon name="Plus" size={16} className="mr-2" />
                    Add Your First Dish
                  </Button>
                  <Button variant="outline" onClick={() => setShowBulkImport(true)}>
                    <Icon name="Upload" size={16} className="mr-2" />
                    Import Recipes
                  </Button>
                </div>
            }
            </div>
          }
        </div>
      </main>
      {/* Modals */}
      <DishForm
        dish={editingDish}
        onSave={handleSaveDish}
        onCancel={() => {
          setShowDishForm(false);
          setEditingDish(null);
        }}
        isOpen={showDishForm} />

      <DishDetailModal
        dish={selectedDish}
        isOpen={showDishDetail}
        onClose={() => {
          setShowDishDetail(false);
          setSelectedDish(null);
        }}
        onEdit={handleEditDish} />

      <BulkImportModal
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImport={handleBulkImport} />

      {/* Floating Action Button */}
      <FloatingActionButton onAction={handleFloatingAction} />
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>);

};

export default DishLibrary;